import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { sankey, sankeyLinkHorizontal } from 'd3-sankey'

interface D3SankeyChartProps {
  funnelData: any
  chartData: any[]
  funnelConfig: any
}

interface SankeyNode {
  id: string
  name: string
  category: 'entry' | 'step' | 'empty' | 'empty_conversion' | 'conversion' | 'friction'
  users: number
  hasData?: boolean
  value?: number  // Explicit value for D3 Sankey (prevents auto-calculation)
  layer?: number  // For positioning: 0 = main funnel, 1 = friction row
  entry_sources?: {
    external: Record<string, number>
    internal: Record<string, number>
  }
  dominant_source_type?: 'external' | 'internal'  // For color-coding
  top_source?: string  // Top source name for annotation
}

interface SankeyLink {
  source: string
  target: string
  value: number
  type: 'entry' | 'progression' | 'ghost' | 'dropoff' | 'stepback' | 'stalling'
  metadata?: {
    progressionRate?: number
    actualValue?: number
    entryShare?: number
    conversionRate?: number
    frictionType?: 'dropoff' | 'stepback' | 'stalling'
    fromStep?: string  // Which step the dropout came from
  }
}

interface SankeyData {
  nodes: SankeyNode[]
  links: SankeyLink[]
}

// ============================================================================
// DATA PREPARATION: Merge funnel configuration with actual analytics data
// ============================================================================

function mergeConfigWithData(configSteps: any[], actualData: any[]): any[] {
  if (!configSteps || configSteps.length === 0) {
    return actualData || []
  }

  const dataMap = new Map()
  actualData?.forEach((step) => {
    dataMap.set(step.stepId, step)
    dataMap.set(step.name, step)
    if (step.isConversion) {
      dataMap.set('conversion', step)
    }
  })

  return configSteps.map((configStep, index) => {
    const actualStep = dataMap.get(configStep.id) ||
                      dataMap.get(configStep.name) ||
                      (configStep.type === 'conversion' ? dataMap.get('conversion') : null)

    if (actualStep) {
      return {
        ...actualStep,
        name: configStep.name,
        isConversion: configStep.type === 'conversion',
        stepType: configStep.type,
        hasData: true
      }
    }

    // Empty step placeholder
    return {
      name: configStep.name,
      users: 0,
      usersEntered: 0,
      conversionsFromEntry: 0,
      hits: 0,
      entryShare: 0,
      entryConversionRate: 0,
      progressionRate: 0,
      dropOffRate: 0,
      stepId: configStep.id,
      stepNumber: index + 1,
      isConversion: configStep.type === 'conversion',
      stepType: configStep.type,
      isLastStep: index === configSteps.length - 1,
      hasData: false
    }
  })
}

// ============================================================================
// SANKEY DATA MAPPING: Transform funnel data into Sankey nodes and links
// ============================================================================

function buildSankeyData(stepData: any[]): SankeyData {
  const nodes: SankeyNode[] = []
  const links: SankeyLink[] = []

  // STEP 1: Create entry nodes for multi-entry funnel (skip first step - it's the default entry)
  // IMPORTANT: Only show EXTERNAL entries (traffic from outside the site)
  // Internal navigation (from homepage, categories, etc.) is NOT a true "entry point"

  // Calculate total traffic for significance filtering
  const totalTraffic = stepData.reduce((sum, step) => sum + (Number(step.users) || 0), 0)
  const significanceThreshold = totalTraffic * 0.01 // 1% of total traffic

  stepData.forEach((step, index) => {
    if (index === 0) return // First step is the default funnel start, not a "direct entry"

    // FILTER 1: Only create entry nodes for steps where direct entry is realistic
    // Users cannot directly enter at event or conversion steps
    const stepType = step.stepType || step.type
    const isEventOrConversion = ['event', 'conversion'].includes(stepType)

    if (isEventOrConversion) {
      return // Skip entry nodes for events and conversions
    }

    // Extract entry sources - only use EXTERNAL entries for entry nodes
    const entry_sources = step.entry_sources
    const externalTotal = entry_sources?.external?.total || 0

    // Only show external entries (true landing points from outside the site)
    // Internal navigation is part of the funnel flow, not an entry point
    if (externalTotal > 0 && externalTotal >= significanceThreshold) {
      // Find top external source
      let top_source: string | undefined
      if (entry_sources?.external) {
        const externalSources = { ...entry_sources.external }
        delete externalSources.total

        const sortedSources = Object.entries(externalSources)
          .filter(([_, count]) => (count as number) > 0)
          .sort(([, a], [, b]) => (b as number) - (a as number))

        if (sortedSources.length > 0) {
          top_source = sortedSources[0][0]
        }
      }

      nodes.push({
        id: `entry_${index}`,
        name: `IN ${step.name}`,  // Label with target step name
        category: 'entry',
        users: externalTotal,  // Only external entries
        value: externalTotal,  // Explicitly set node value
        layer: 1,  // Position below main funnel
        entry_sources,
        dominant_source_type: 'external',  // Always external now
        top_source
      })
    }
  })

  // STEP 2: Create step nodes (all configured steps, even with 0 users)
  stepData.forEach((step, index) => {
    const users = Number(step.users) || 0
    const hasUsers = users > 0

    let category: SankeyNode['category'] = 'empty'
    if (step.isConversion) {
      category = hasUsers ? 'conversion' : 'empty_conversion'
    } else if (hasUsers) {
      category = 'step'
    }

    nodes.push({
      id: `step_${index}`,
      name: step.name || `Step ${index + 1}`,
      category,
      users,
      hasData: hasUsers,
      value: users,  // Explicitly set node value to prevent D3 auto-calculation
      layer: 0  // Main funnel layer
    })
  })

  // STEP 3: Create single friction node (positioned at bottom)
  // Note: "Salió del Funnel" is more accurate than "Abandono" because these sessions
  // may have navigated to other pages on the site, not necessarily left entirely
  nodes.push({
    id: 'friction',
    name: 'Salió del Funnel',
    category: 'friction',
    users: 0,  // Will be calculated from links
    hasData: false,
    value: 0,
    layer: 2  // Bottom layer
  })

  // STEP 4: Create entry links (from entry nodes to their target steps)
  // Only create links for EXTERNAL entries (matching the entry nodes)
  stepData.forEach((step, index) => {
    if (index === 0) return // Skip first step - it's the default funnel start

    const externalTotal = step.entry_sources?.external?.total || 0
    if (externalTotal > 0) {
      // Check if we created an entry node for this step (passes significance threshold)
      const entryNodeExists = nodes.some(n => n.id === `entry_${index}`)
      if (entryNodeExists) {
        links.push({
          source: `entry_${index}`,
          target: `step_${index}`,
          value: externalTotal,
          type: 'entry',
          metadata: {
            entryShare: Number(step.entryShare) || 0,
            conversionRate: Number(step.entryConversionRate) || 0
          }
        })
      }
    }
  })

  // STEP 5: Create progression links AND friction links
  // Use ACTUAL backend values (sessions_progressed) when available,
  // fall back to inference only if backend data is missing

  stepData.forEach((step, index) => {
    if (index >= stepData.length - 1) return // Skip last step (conversion)

    const currentUsers = Number(step.users) || 0
    const nextIndex = index + 1

    // Use backend's sessions_progressed if available, otherwise infer
    let flowToNext: number
    if (step.sessions_progressed !== undefined && step.sessions_progressed !== null) {
      // Use actual backend value
      flowToNext = Number(step.sessions_progressed) || 0
    } else {
      // Fallback: infer from user counts
      const nextStep = stepData[nextIndex]
      const nextUsers = Number(nextStep?.users) || 0
      const nextEntered = Number(nextStep?.usersEntered) || 0
      flowToNext = Math.min(currentUsers, Math.max(0, nextUsers - nextEntered))
    }

    // Create progression link to next step
    if (flowToNext > 0) {
      links.push({
        source: `step_${index}`,
        target: `step_${nextIndex}`,
        value: flowToNext,
        type: 'progression',
        metadata: {
          progressionRate: currentUsers > 0 ? (flowToNext / currentUsers) * 100 : 0,
          actualValue: flowToNext
        }
      })
    }

    // Calculate drop-off: users who didn't progress
    // Use backend's sessions_dropped if available
    let dropOffValue: number
    if (step.sessions_dropped !== undefined && step.sessions_dropped !== null) {
      dropOffValue = Number(step.sessions_dropped) || 0
    } else {
      dropOffValue = Math.max(0, currentUsers - flowToNext)
    }

    if (dropOffValue > 0) {
      links.push({
        source: `step_${index}`,
        target: 'friction',
        value: dropOffValue,
        type: 'dropoff',
        metadata: {
          progressionRate: 0,
          actualValue: dropOffValue,
          frictionType: 'dropoff',
          fromStep: step.name
        }
      })

      // Update friction node value (accumulate all drop-offs)
      const frictionNode = nodes.find(n => n.id === 'friction')
      if (frictionNode) {
        frictionNode.users = (frictionNode.users || 0) + dropOffValue
        frictionNode.value = (frictionNode.value || 0) + dropOffValue
        frictionNode.hasData = true
      }
    }
  })

  return { nodes, links }
}

// ============================================================================
// D3 SANKEY RENDERING
// ============================================================================

export function D3SankeyChart({ funnelData, chartData, funnelConfig }: D3SankeyChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (!svgRef.current) return

    // Prepare complete step data
    const completeStepData = funnelConfig?.steps
      ? mergeConfigWithData(funnelConfig.steps, chartData)
      : chartData

    if (!completeStepData || completeStepData.length === 0) return

    // Clear previous rendering
    d3.select(svgRef.current).selectAll('*').remove()

    // Build Sankey data structure
    const sankeyData = buildSankeyData(completeStepData)

    if (sankeyData.nodes.length === 0) {
      console.warn('No nodes available for Sankey chart')
      return
    }

    // Setup dimensions - responsive based on container
    const containerWidth = svgRef.current.parentElement?.clientWidth || 800
    const width = containerWidth // Use actual container width, no minimum
    const height = width * 0.5 // 50% aspect ratio for better proportions

    // Margins as percentage of dimensions
    const margin = {
      top: height * 0.10,     // 10% of height for top labels
      right: width * 0.05,    // 5% of width - better side spacing
      bottom: height * 0.16,  // 16% of height for bottom entry labels
      left: width * 0.05      // 5% of width - better side spacing
    }

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)

    // Create Sankey generator with custom node sorting
    // Calculate responsive node dimensions for 5-7 horizontal nodes
    const availableWidth = width - margin.left - margin.right
    const nodeWidth = width * 0.05 // 5% of width - thicker nodes, more visible
    const estimatedMaxNodes = 7 // worst case
    const nodePadding = Math.max(60, (availableWidth - estimatedMaxNodes * nodeWidth) / (estimatedMaxNodes + 1))

    const sankeyGenerator = sankey<any, any>()
      .nodeWidth(nodeWidth)
      .nodePadding(nodePadding)
      .nodeSort((a: any, b: any) => {
        // Define sort order: main funnel steps first, then entry nodes, then friction, then conversion
        const categoryOrder = {
          'step': 0,
          'entry': 1,
          'friction': 2,
          'conversion': 3
        }

        const orderA = categoryOrder[a.category as keyof typeof categoryOrder] ?? 999
        const orderB = categoryOrder[b.category as keyof typeof categoryOrder] ?? 999

        if (orderA !== orderB) {
          return orderA - orderB
        }

        // Within same category, sort by ID to maintain funnel order
        return (a.id || '').localeCompare(b.id || '')
      })
      .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]])

    // Transform string IDs to indices for D3 Sankey
    const nodeMap = new Map()
    sankeyData.nodes.forEach((node, index) => {
      nodeMap.set(node.id, index)
    })

    const processedLinks = sankeyData.links
      .map(link => ({
        ...link,
        source: nodeMap.get(link.source),
        target: nodeMap.get(link.target)
      }))
      .filter(link =>
        link.source !== undefined &&
        link.target !== undefined &&
        !isNaN(link.value) &&
        isFinite(link.value) &&
        link.value > 0
      )

    const processedData = {
      nodes: sankeyData.nodes,
      links: processedLinks
    }

    // Generate layout
    let sankeyLayout: any
    try {
      sankeyLayout = sankeyGenerator(processedData)
    } catch (error) {
      console.error('Error generating Sankey layout:', error)
      return
    }

    // Define colors
    const colors = {
      entry: '#94a3b8',
      entry_external: '#3b82f6',  // Blue for external-dominant entries
      entry_internal: '#10b981',  // Green for internal-dominant entries
      step: '#10b981',
      empty: '#f3f4f6',
      empty_conversion: '#fecaca',
      conversion: '#ef4444',
      friction: '#fca5a5',  // Light red for friction nodes
      entryLink: '#94a3b8',
      progressionLink: '#6366f1',
      ghostLink: '#e5e7eb',
      dropoffLink: '#fca5a5',  // Single color for all drop-offs
      stepbackLink: '#f59e0b',  // Orange for step-back
      stallingLink: '#eab308'   // Yellow for stalling
    }

    // Create tooltip
    const tooltip = d3.select('body').selectAll('.d3-tooltip')
      .data([null])
      .enter()
      .append('div')
      .attr('class', 'd3-tooltip')
      .style('position', 'absolute')
      .style('background', 'white')
      .style('border', '1px solid #ccc')
      .style('border-radius', '4px')
      .style('padding', '8px')
      .style('font-size', '12px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', 1000)

    // Draw links
    svg.append('g')
      .attr('class', 'links')
      .selectAll('path')
      .data(sankeyLayout.links)
      .enter()
      .append('path')
      .attr('d', sankeyLinkHorizontal() as any)
      .attr('stroke', (d: any) => {
        // Friction links - single color
        if (d.type === 'dropoff') return colors.dropoffLink
        if (d.type === 'stepback') return colors.stepbackLink
        if (d.type === 'stalling') return colors.stallingLink
        // Regular links
        if (d.type === 'entry') return colors.entryLink
        if (d.type === 'ghost') return colors.ghostLink
        return colors.progressionLink
      })
      .attr('stroke-width', (d: any) => {
        if (d.type === 'ghost') return 2
        return Math.max(1, d.width)
      })
      .attr('stroke-opacity', (d: any) => {
        if (d.type === 'dropoff' || d.type === 'stepback' || d.type === 'stalling') return 0.5
        if (d.type === 'ghost') return 0.3
        return 0.5
      })
      .attr('stroke-dasharray', (d: any) => {
        if (d.type === 'dropoff') return '3,3'
        if (d.type === 'stepback') return '5,2'
        if (d.type === 'stalling') return '2,2'
        if (d.type === 'ghost') return '4,4'
        return 'none'
      })
      .attr('fill', 'none')
      .on('mouseover', function(event: any, d: any) {
        d3.select(this)
          .attr('stroke-opacity', 0.8)
          .attr('stroke-width', Math.max(2, d.width + 2))

        let tooltipContent = `<strong>${d.source.name} → ${d.target.name}</strong><br/>`

        if (d.type === 'ghost') {
          tooltipContent += `Sin flujo de sesiones en este período<br/>`
          tooltipContent += `<em>Conexión estructural del funnel</em>`
        } else if (d.type === 'dropoff') {
          tooltipContent += `<strong>Sesiones:</strong> ${d.value}<br/>`
          tooltipContent += `<span style="color: #ef4444;">Abandono desde: ${d.metadata?.fromStep || d.source.name}</span><br/>`
          tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Una misma sesión puede aparecer en múltiples pasos</em>`
        } else if (d.type === 'stepback') {
          tooltipContent += `<strong>Sesiones:</strong> ${d.value}<br/>`
          tooltipContent += `<span style="color: #f59e0b;">Retroceso al paso anterior</span><br/>`
          tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Una misma sesión puede aparecer en múltiples pasos</em>`
        } else if (d.type === 'stalling') {
          tooltipContent += `<strong>Sesiones:</strong> ${d.value}<br/>`
          tooltipContent += `<span style="color: #eab308;">Tiempo excesivo en este paso</span><br/>`
          tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Una misma sesión puede aparecer en múltiples pasos</em>`
        } else {
          tooltipContent += `<strong>Sesiones:</strong> ${d.value}<br/>`

          if (d.type === 'entry' && d.metadata?.entryShare) {
            tooltipContent += `Entrada: ${d.metadata.entryShare}% del tráfico<br/>`
            if (d.metadata.conversionRate > 0) {
              tooltipContent += `Conversión: ${d.metadata.conversionRate}%<br/>`
            }
            tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Sesiones que iniciaron en este paso</em>`
          } else if (d.type === 'progression' && d.metadata?.progressionRate) {
            tooltipContent += `Progresión: ${d.metadata.progressionRate.toFixed(1)}%<br/>`
            tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">% de sesiones que continuaron al siguiente paso</em>`
          }
        }

        tooltip.html(tooltipContent)
          .style('opacity', 1)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px')
      })
      .on('mouseout', function(_event: any, d: any) {
        d3.select(this)
          .attr('stroke-opacity', 0.5)
          .attr('stroke-width', Math.max(1, d.width))

        tooltip.style('opacity', 0)
      })

    // Draw nodes - adjust position for minimum height
    svg.append('g')
      .attr('class', 'nodes')
      .selectAll('rect')
      .data(sankeyLayout.nodes)
      .enter()
      .append('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => {
        const calculatedHeight = d.y1 - d.y0
        const actualHeight = Math.max(20, calculatedHeight)
        // Center the node if height was adjusted
        return d.y0 - (actualHeight - calculatedHeight) / 2
      })
      .attr('height', (d: any) => Math.max(20, d.y1 - d.y0))  // Minimum 20px height
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => {
        switch (d.category) {
          case 'friction': return colors.friction
          case 'entry': {
            // Color-code by dominant source type
            if (d.dominant_source_type === 'external') return colors.entry_external
            if (d.dominant_source_type === 'internal') return colors.entry_internal
            return colors.entry // Fallback for legacy data
          }
          case 'conversion': return colors.conversion
          case 'empty': return colors.empty
          case 'empty_conversion': return colors.empty_conversion
          default: return colors.step
        }
      })
      .attr('stroke', (d: any) => {
        if (d.category === 'friction') return '#f87171'
        if (d.category === 'empty' || d.category === 'empty_conversion') return '#d1d5db'
        return '#ffffff'
      })
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', (d: any) => {
        if (d.category === 'empty' || d.category === 'empty_conversion') return '4,2'
        return 'none'
      })
      .attr('opacity', (d: any) => {
        if (d.category === 'empty' || d.category === 'empty_conversion') return 0.6
        return 1.0
      })
      .attr('rx', 4)
      .attr('ry', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function(event: any, d: any) {
        d3.select(this)
          .attr('stroke-width', 3)
          .attr('stroke', '#1f2937')

        let tooltipContent = `<strong>${d.name}</strong><br/>`

        if (d.category === 'friction') {
          tooltipContent += `<strong>Sesiones:</strong> ${d.users || d.value}<br/>`
          tooltipContent += '<span style="color: #ef4444;">Sesiones que salieron del funnel</span><br/>'
          tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Incluye usuarios que abandonaron el sitio o navegaron a páginas fuera del funnel (ej: volver al inicio, ver otras categorías)</em>`
        } else if (d.category === 'empty' || d.category === 'empty_conversion') {
          tooltipContent += `Sin sesiones en este período<br/>`
          if (d.category === 'empty_conversion') {
            tooltipContent += '<em>Paso de conversión configurado</em>'
          } else {
            tooltipContent += '<em>Paso configurado esperando tráfico</em>'
          }
        } else {
          tooltipContent += `<strong>Sesiones:</strong> ${d.users || d.value}<br/>`

          if (d.category === 'entry') {
            tooltipContent += '<span style="color: #3b82f6;">Punto de entrada directo</span><br/>'

            // Show ONLY external sources (entry nodes = external traffic only)
            if (d.entry_sources?.external) {
              const external = d.entry_sources.external
              const externalTotal = external.total || 0

              tooltipContent += '<br/><strong>Fuentes de Tráfico:</strong><br/>'
              // Show top 5 external sources
              const topExternal = Object.entries(external)
                .filter(([key]) => key !== 'total')
                .filter(([_, val]) => (val as number) > 0)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 5)

              topExternal.forEach(([source, count]) => {
                const pct = externalTotal > 0 ? ((count as number) / externalTotal * 100).toFixed(0) : 0
                const label = source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, ' ')
                tooltipContent += `&nbsp;&nbsp;• ${label}: ${count} (${pct}%)<br/>`
              })

              tooltipContent += `<br/><em style="font-size: 0.85em; color: #9ca3af;">Sesiones que aterrizaron directamente en este paso desde fuera del sitio</em>`
            } else {
              tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Tráfico externo que aterrizó en este paso</em>`
            }
          } else if (d.category === 'conversion') {
            tooltipContent += '<span style="color: #10b981;">Paso de conversión final</span><br/>'
            tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Sesiones que completaron el funnel exitosamente</em>`
          } else {
            tooltipContent += `<em style="font-size: 0.85em; color: #9ca3af;">Total de sesiones en este paso del funnel</em>`
          }
        }

        tooltip.html(tooltipContent)
          .style('opacity', 1)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px')
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('stroke', '#ffffff')

        tooltip.style('opacity', 0)
      })

    // Calculate responsive font sizes - smaller for compact layout
    const labelFontSize = Math.max(10, nodeWidth * 0.24) // ~11px at 48px node
    const countFontSize = Math.max(9, nodeWidth * 0.20)  // ~10px at 48px node

    // Add node labels - positioned by category to prevent overlaps
    svg.append('g')
      .attr('class', 'labels')
      .selectAll('text')
      .data(sankeyLayout.nodes)
      .enter()
      .append('text')
      .attr('x', (d: any) => (d.x0 + d.x1) / 2)
      .attr('y', (d: any) => {
        // Adjust for minimum height
        const calculatedHeight = d.y1 - d.y0
        const actualHeight = Math.max(20, calculatedHeight)
        const heightAdjustment = (actualHeight - calculatedHeight) / 2

        // Position labels based on node category
        if (d.category === 'entry' || d.category === 'friction') {
          // Entry and friction nodes: label BELOW (adjust for centered node)
          return d.y1 + heightAdjustment + (height * 0.04)
        } else {
          // Step and conversion nodes: label ABOVE (adjust for centered node)
          return d.y0 - heightAdjustment - (height * 0.04)
        }
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', (d: any) => {
        // Align text baseline for better vertical centering
        if (d.category === 'entry' || d.category === 'friction') {
          return 'hanging'  // Text hangs below the position (for labels below nodes)
        } else {
          return 'auto'  // Default alignment (for labels above nodes)
        }
      })
      .style('font-family', 'system-ui, sans-serif')
      .style('font-size', `${labelFontSize}px`)
      .style('font-weight', '600')
      .style('fill', '#1f2937')
      .text((d: any) => d.name)

    // Add user counts
    svg.append('g')
      .attr('class', 'user-counts')
      .selectAll('text')
      .data(sankeyLayout.nodes)
      .enter()
      .append('text')
      .attr('x', (d: any) => (d.x0 + d.x1) / 2)
      .attr('y', (d: any) => {
        // Use the visual center, not the data center (accounts for minimum height adjustment)
        return (d.y1 + d.y0) / 2
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .style('font-family', 'monospace')  // Monospace for number alignment
      .style('font-size', `${countFontSize}px`)
      .style('fill', (d: any) => d.hasData === false ? '#9ca3af' : '#ffffff')
      .style('font-weight', '600')
      .text((d: any) => `${d.users || d.value || 0}`)

    // Add source annotations for entry nodes
    svg.append('g')
      .attr('class', 'source-annotations')
      .selectAll('text')
      .data(sankeyLayout.nodes.filter((d: any) => d.category === 'entry' && d.top_source))
      .enter()
      .append('text')
      .attr('x', (d: any) => (d.x0 + d.x1) / 2)
      .attr('y', (d: any) => {
        const calculatedHeight = d.y1 - d.y0
        const actualHeight = Math.max(20, calculatedHeight)
        const heightAdjustment = (actualHeight - calculatedHeight) / 2
        return d.y1 + heightAdjustment + (height * 0.08) // Below the step name
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .style('font-family', 'system-ui, sans-serif')
      .style('font-size', `${labelFontSize * 0.8}px`)
      .style('font-weight', '500')
      .style('fill', '#6b7280')
      .style('font-style', 'italic')
      .text((d: any) => {
        if (!d.top_source) return ''
        const label = d.top_source.charAt(0).toUpperCase() + d.top_source.slice(1).replace(/_/g, ' ')
        return `via ${label}`
      })

    // Add legend - positioned at bottom center with better spacing
    const legendWidth = 700 // Total width for all legend items (increased for new items)
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${(width - legendWidth) / 2}, ${height - margin.bottom * 0.25})`)

    const legendItems = [
      { color: colors.entry_external, label: 'Entradas Externas' },
      { color: colors.step, label: 'Pasos del Funnel' },
      { color: colors.friction, label: 'Salió del Funnel' },
      { color: colors.conversion, label: 'Conversión' },
      { color: colors.empty, label: 'Sin Usuarios', dashed: true }
    ]

    const legendGroups = legend.selectAll('.legend-item')
      .data(legendItems)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (_d, i) => `translate(${i * 115}, 0)`) // Horizontal layout with tighter spacing

    legendGroups.append('rect')
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', d => d.color)
      .attr('stroke', (d: any) => d.dashed ? '#9ca3af' : 'none')
      .attr('stroke-dasharray', (d: any) => d.dashed ? '2,1' : 'none')
      .attr('stroke-width', (d: any) => d.dashed ? 1 : 0)

    legendGroups.append('text')
      .attr('x', 18)
      .attr('y', 6)
      .attr('dy', '0.35em')
      .style('font-family', 'system-ui, sans-serif')
      .style('font-size', '10px')
      .style('fill', '#6b7280')
      .text(d => d.label)

  }, [funnelData, chartData, funnelConfig])

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px] text-muted-foreground">
        <p>Esperando datos del funnel...</p>
      </div>
    )
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg ref={svgRef} className="border border-gray-200 rounded max-w-full" style={{ display: 'block' }} />
    </div>
  )
}
